import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ShieldCheck, Flower } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!username || !password) {
      toast.error('Please enter username and password');
      return;
    }

    try {
      setLoading(true);
      await axios.post(
        `${API}/admin/login`,
        { username, password },
        { withCredentials: true }
      );
      
      toast.success('Admin login successful!');
      navigate('/admin/flower-exchange');
    } catch (error) {
      console.error('Admin login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid admin credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-6">
            <Flower size={40} weight="fill" className="text-primary" />
            <span className="text-3xl font-heading font-bold">Bloom & Bond</span>
          </div>
          <ShieldCheck size={64} weight="fill" className="text-accent mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-heading font-bold mb-2">Admin Login</h1>
          <p className="text-muted-foreground">Enter your admin credentials to continue</p>
        </div>

        <form onSubmit={handleLogin} className="bg-white/80 backdrop-blur-md border border-white/20 shadow-lg rounded-3xl p-8 space-y-6">
          <div className="space-y-2">
            <Label htmlFor="username" data-testid="username-label">Username</Label>
            <Input
              id="username"
              data-testid="admin-username-input"
              type="text"
              placeholder="Enter admin username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="rounded-xl"
              autoComplete="username"
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" data-testid="password-label">Password</Label>
            <Input
              id="password"
              data-testid="admin-password-input"
              type="password"
              placeholder="Enter admin password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-xl"
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <Button
            data-testid="admin-login-button"
            type="submit"
            disabled={loading}
            className="w-full rounded-full bg-primary hover:bg-primary-hover font-bold py-6 text-lg"
          >
            {loading ? 'Logging in...' : 'Login as Admin'}
          </Button>

          <div className="text-center">
            <Button
              data-testid="back-to-home-button"
              type="button"
              onClick={() => navigate('/')}
              variant="ghost"
              className="text-sm"
            >
              ← Back to Home
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}
